/**
 * a custom module for pasting in links, and realtime conversion while typing
 * - current link selection is available in
 */
import Quill from 'quill';
import Delta from 'quill-delta';

import { cleanRawUrl, normalizer, regexBank } from './utilities';

interface PrettyLinkInterface {
  registerTypeListener: () => void;
  registerPasteListener: () => void;
  registerBlurListener: () => void;
  checkTextForUrl: () => void;
}
export class PrettyLink implements PrettyLinkInterface {
  quill: Quill;
  constructor(quill) {
    this.quill = quill;
    this.registerTypeListener();
    this.registerPasteListener();
    this.registerBlurListener();
    this.checkTextForUrl();
  }
  // on paste listener
  registerPasteListener() {
    // make sure the matcher is implemented
    this.quill.clipboard.addMatcher('A', (node, delta) => {
      const href = node.getAttribute('href');
      const attributes = delta.ops[0].attributes;
      if (attributes != null && attributes.link != null) {
        attributes.link = href;
      }
      return delta;
    });

    // now add the custom matcher
    this.quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
      if (typeof node.data !== 'string') {
        return;
      }
      const newDelta = new Delta();
      regexBank.globalUrlMatcher.lastIndex = 0;
      let urlResult = regexBank.globalUrlMatcher.exec(node.data);
      let index = 0;
      // if there is a match, handle inserting it
      const handleMatch = (result) => {
        const head = node.data.substring(index, result.index);
        newDelta.insert(head);
        const match = result[0];
        // the current selection is saved to the window object via the quillHooks.ts
        newDelta.insert(window['_selection'] || cleanRawUrl(match), {
          link: normalizer(match),
        });
        index = regexBank.globalUrlMatcher.lastIndex;
        return regexBank.globalUrlMatcher.exec(node.data);
      };
      // test things
      while (urlResult !== null) {
        urlResult = handleMatch(urlResult);
      }
      if (index > 0) {
        const tail = node.data.substring(index);
        newDelta.insert(tail);
        delta.ops = newDelta.ops;
      }
      return delta;
    });
  }
  // on real time typing listener
  registerTypeListener() {
    this.quill.on('text-change', (delta) => {
      const ops = delta.ops;
      if (!ops || ops.length < 1 || ops.length > 2) {
        return;
      }
      const lastOp = ops[ops.length - 1];
      if (
        !lastOp.insert ||
        typeof lastOp.insert !== 'string' ||
        !lastOp.insert.match(/\s/)
      ) {
        return;
      }
      this.checkTextForUrl(!!lastOp.insert.match(/ |\t/));
    });
  }
  // on real time typing listener
  registerBlurListener() {
    this.quill.root.addEventListener('blur', () => {
      this.checkTextForUrl();
    });
  }
  // check for url
  checkTextForUrl(onWhiteSpace = false) {
    // get the selection
    const selection = this.quill.getSelection();
    if (!selection) {
      return;
    }
    // get the leaf
    const [leaf] = this.quill.getLeaf(selection.index);
    const leafIndex = this.quill.getIndex(leaf);

    if (!leaf.text) {
      return;
    }

    // We only care about the leaf until the current cursor position
    const relevantLength = selection.index - leafIndex;
    const text = leaf.text.slice(0, relevantLength);
    if (!text || leaf.parent.domNode.localName === 'a') {
      return;
    }

    const nextLetter = leaf.text[relevantLength];
    // Do not proceed if we are in the middle of a word
    if (nextLetter != null && nextLetter.match(/\S/)) {
      return;
    }

    const bailOutEndingRegex = onWhiteSpace ? /\s\s$/ : /\s$/;
    if (text.match(bailOutEndingRegex)) {
      return;
    }

    const urlMatches = text.match(regexBank.globalUrlMatcher);
    if (urlMatches) {
      this.handleMatches(leafIndex, text, urlMatches);
    }
  }
  handleMatches(leafIndex, text, matches) {
    const match = matches.pop();
    const matchIndex = text.lastIndexOf(match);
    const after = text.split(match).pop();
    if (after.match(/\S/)) {
      return;
    }
    this.updateText(leafIndex + matchIndex, match.trim());
  }
  updateText(index, string) {
    const ops = new Delta()
      .retain(index)
      .retain(string.length, { link: normalizer(string) });
    this.quill.updateContents(ops);
  }
}
